import { Box, Stack, Typography } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { isFederalGeneratorDiscrepancy } from "encamp-shared/src/facility/generatorThresholdComparison";
import {
  federalGeneratorStatusRank,
  stateGeneratorStatusRank,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import { stateDoesNotFollowFederalGeneratorStatus } from "encamp-shared/src/waste/constants";

import { ComparisonArrow } from "./ComparisonArrow";
import { calculatedStatusLabel, comparisonDescription } from "./generatorStatusDisplay";
import { formatPeriodMonth } from "./periodFormat";

export type ComparisonValues = {
  state?: string | null;
  reportedFederalStatus?: string | null;
  calculatedFederalStatus?: string | null;
  reportedStateStatus?: string | null;
  reportedStateStatusLabel?: string | null;
  calculatedStateStatus?: string | null;
  stateDirection?: string | null;
  direction?: string | null;
  calculationAvailable?: boolean;
};

function ComparisonRow({
  label,
  reported,
  reportedLabel,
  calculated,
  rank,
  direction,
  calculationAvailable,
}: {
  label: string;
  reported?: string | null;
  reportedLabel?: string | null;
  calculated?: string | null;
  rank: (status: string | null) => number;
  direction?: string | null;
  calculationAvailable?: boolean;
}) {
  const comparison = comparisonDescription(
    reported,
    calculated,
    rank,
    direction,
    calculationAvailable
  );
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
      >
        <Typography variant="subtitle2">{label}</Typography>
        <StatusChip color={comparison.color} label={comparison.label} />
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Reported to RCRAInfo
          </Typography>
          <Typography variant="body1">
            {reportedLabel ??
              (reported ? stateWasteGeneratorStatusToLabel(reported) : "Unavailable")}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Calculated from waste data
          </Typography>
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            {calculatedStatusLabel(calculated, Boolean(direction) || Boolean(calculationAvailable))}
            <ComparisonArrow relation={comparison.relation} />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/** Compare each jurisdiction against its own reported baseline for the same month. */
export function StatusComparison({
  values,
  stateMonth,
}: {
  values: ComparisonValues;
  /** Set when the state comparison comes from an earlier month than the federal one. */
  stateMonth?: string;
}) {
  const showState =
    Boolean(values.stateDirection) || stateDoesNotFollowFederalGeneratorStatus(values.state);
  const stateLabel = values.state ? `${values.state} state` : "State";
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: showState ? "1fr 1fr" : "1fr" },
        gap: 1.5,
      }}
    >
      <ComparisonRow
        label="Federal"
        reported={values.reportedFederalStatus}
        calculated={values.calculatedFederalStatus}
        rank={federalGeneratorStatusRank}
        calculationAvailable={values.calculationAvailable}
        direction={
          values.direction && isFederalGeneratorDiscrepancy(values)
            ? federalGeneratorStatusRank(values.calculatedFederalStatus) >
              federalGeneratorStatusRank(values.reportedFederalStatus)
              ? "UPWARD"
              : "DOWNWARD"
            : null
        }
      />
      {showState && (
        <ComparisonRow
          label={stateMonth ? `${stateLabel} · ${formatPeriodMonth(stateMonth)}` : stateLabel}
          reported={values.reportedStateStatus}
          reportedLabel={values.reportedStateStatusLabel}
          calculated={values.calculatedStateStatus}
          rank={stateGeneratorStatusRank}
          calculationAvailable={values.calculationAvailable}
          direction={values.stateDirection}
        />
      )}
    </Box>
  );
}
